import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Modal from '../../components/Modal';
import {
  createStaffUser,
  deleteStaffUser,
  listStaffUsers,
  updateStaffUserRole,
  lookupStaffUserByEmail,
  type StaffRole,
  type StaffUserRecord,
} from '../../services/staff';
import { listRestaurants, type RestaurantRecord } from '../../services/restaurants';

const createStaffSchema = z.object({
  email: z.string().email('Geçerli bir e-posta girin'),
  password: z
    .preprocess(
      (value) => (typeof value === 'string' && value.trim().length === 0 ? undefined : value),
      z.string().min(8, 'Parola en az 8 karakter olmalı').optional(),
    )
    .optional(),
  fullName: z.string().optional(),
  phone: z.string().optional(),
  role: z.enum(['admin', 'staff', 'manager', 'driver']).default('staff'),
  inviteInsteadOfPassword: z.boolean().default(false),
  restaurants: z.array(z.string()).default([]),
}).superRefine((val, ctx) => {
  if ((val.role === 'staff' || val.role === 'manager') && val.restaurants.length === 0) {
    ctx.addIssue({
      path: ['restaurants'],
      code: 'custom',
      message: 'Personel için en az bir restoran seçin.',
    });
  }
  if (val.inviteInsteadOfPassword === false && !val.password) {
    ctx.addIssue({
      path: ['password'],
      code: 'custom',
      message: 'Davetiye gönderilmiyorsa parola zorunludur.',
    });
  }
});

type StaffFormValues = z.infer<typeof createStaffSchema>;

const DEFAULT_VALUES: StaffFormValues = {
  email: '',
  password: '',
  fullName: '',
  phone: '',
  role: 'staff',
  inviteInsteadOfPassword: true,
  restaurants: [],
};

export default function StaffManager() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
  const [existingUser, setExistingUser] = useState<StaffUserRecord | null>(null);
  const { data: restaurants = [] } = useQuery<RestaurantRecord[]>({
    queryKey: ['restaurants', 'staff-assignment'],
    queryFn: listRestaurants,
  });

  const {
    data: staff,
    error,
    isError,
    isLoading,
  } = useQuery({
    queryKey: ['people', 'staff'],
    queryFn: listStaffUsers,
    retry: false,
  });

  const form = useForm<StaffFormValues>({
    resolver: zodResolver(createStaffSchema),
    defaultValues: DEFAULT_VALUES,
  });
  const roleValue = form.watch('role');

  useEffect(() => {
    if (roleValue === 'driver' || roleValue === 'admin') {
      form.setValue('restaurants', [], { shouldDirty: true });
    }
  }, [form, roleValue]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setExistingUser(null);
    form.reset(DEFAULT_VALUES);
  }, [form]);

  const openCreate = () => {
    setExistingUser(null);
    form.reset(DEFAULT_VALUES);
    setModalOpen(true);
  };

  const openProfile = (staffId: string) => {
    setSelectedStaffId(staffId);
    setProfileModalOpen(true);
  };

  const closeProfile = () => {
    setProfileModalOpen(false);
    setSelectedStaffId(null);
  };

  const createMutation = useMutation({
    mutationFn: createStaffUser,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['people', 'staff'] });
      closeModal();
    },
    onError: (error: unknown) => {
      form.setError('root', {
        type: 'manual',
        message:
          error instanceof Error ? error.message : 'Personel eklenemedi. Alanları kontrol edin.',
      });
    },
  });

  const lookupMutation = useMutation({
    mutationFn: lookupStaffUserByEmail,
    onSuccess: (user) => {
      if (!user) {
        form.setError('email', { message: 'Bu e-posta ile kayıt bulunamadı' });
        setExistingUser(null);
        return;
      }
      form.clearErrors('email');
      setExistingUser(user);
      form.setValue('fullName', user.fullName ?? '', { shouldDirty: true });
      form.setValue('phone', user.phone ?? '', { shouldDirty: true });
      form.setValue('role', user.role === 'unknown' ? 'staff' : user.role, { shouldDirty: true });
      form.setValue('restaurants', user.restaurantSlugs ?? [], { shouldDirty: true });
      form.setValue('inviteInsteadOfPassword', true, { shouldDirty: true });
      form.setValue('password', '', { shouldDirty: false });
    },
    onError: (error: unknown) => {
      form.setError('root', {
        message:
          error instanceof Error
            ? error.message
            : 'E-posta kontrol edilirken bir hata oluştu.',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (staffMember: StaffUserRecord) => deleteStaffUser(staffMember.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['people', 'staff'] });
      setSelectedStaffId(null);
    },
  });

  const roleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: StaffRole }) => updateStaffUserRole(userId, role),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['people', 'staff'] });
    },
    onError: (error: unknown) => {
      window.alert(error instanceof Error ? error.message : 'Rol güncellenemedi.');
    },
  });

  const emailValue = form.watch('email');
  useEffect(() => {
    if (
      existingUser &&
      existingUser.email &&
      emailValue?.toLowerCase() === existingUser.email.toLowerCase()
    ) {
      return;
    }
    if (existingUser && emailValue && existingUser.email?.toLowerCase() !== emailValue.toLowerCase()) {
      setExistingUser(null);
    }
  }, [emailValue, existingUser]);

  const onSubmit = async (values: StaffFormValues) => {
    const isExisting = Boolean(existingUser);
    const payload = {
      email: values.email,
      password: isExisting || values.inviteInsteadOfPassword ? undefined : values.password,
      fullName: values.fullName?.trim() || undefined,
      phone: values.phone?.trim() || undefined,
      role: values.role,
      inviteInsteadOfPassword: isExisting ? true : values.inviteInsteadOfPassword,
      restaurants: values.restaurants ?? [],
    };

    if (!isExisting && !payload.inviteInsteadOfPassword && !payload.password) {
      form.setError('root', { message: 'Password required unless sending invitation' });
      return;
    }

    try {
      await createMutation.mutateAsync(payload);
      form.clearErrors();
    } catch (error: unknown) {
      form.setError('root', {
        message:
          error instanceof Error ? error.message : 'Personel hesabı oluşturulamadı',
      });
    }
  };

  const staffList = useMemo(() => staff ?? [], [staff]);
  const selectedStaff = useMemo(
    () => staffList.find((person) => person.id === selectedStaffId) ?? null,
    [staffList, selectedStaffId]
  );
  const selectedMeta = useMemo(() => {
    if (!selectedStaff) return null;
    const assignments = selectedStaff.assignments ?? [];
    const restaurantAssignments = assignments.filter((assignment) => assignment.role === 'restaurant_staff');
    return {
      role: selectedStaff.role,
      restaurantRole:
        selectedStaff.role === 'admin'
          ? 'admin'
          : selectedStaff.role === 'driver'
            ? 'driver'
          : restaurantAssignments.length > 0
            ? 'restaurant_staff'
            : '—',
      roles: assignments.map((assignment) => assignment.role),
      restaurants: restaurantAssignments.map(
        (assignment) => assignment.restaurantSlug ?? assignment.restaurantName ?? '—',
      ),
    };
  }, [selectedStaff]);

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div>
            <p className="card-title">Personel dizini</p>
            <p className="card-subtitle">
              {isLoading ? 'Personel listesi yükleniyor...' : `${staffList.length} kişi`}
            </p>
          </div>
          <button type="button" className="button primary" onClick={openCreate}>
            + Personel Ekle
          </button>
        </div>

        {isError ? (
          <div className="empty-state">
            <strong>Yonetici erisimi gerekli</strong>
            <p>
              {error instanceof Error
                ? error.message
                : 'Personel hesaplarini yonetmek icin admin-staff edge function yetkilerini dogrulayin.'}
            </p>
          </div>
        ) : staffList.length === 0 ? (
          <div className="empty-state">
            <strong>Henüz personel hesabı yok</strong>
            <p>Panellere erişim için yöneticileri ve ekip üyelerini davet edin.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ad</th>
                  <th>E-posta</th>
                  <th>Rol</th>
                  <th>Son giriş</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {staffList.map((person) => (
                  <tr key={person.id}>
                    <td>
                      <strong>{person.fullName ?? '—'}</strong>
                      <p className="helper-text">{person.phone ?? 'Telefon yok'}</p>
                    </td>
                    <td>{person.email ?? '—'}</td>
                    <td>
                      <select
                        value={person.role === 'unknown' ? 'staff' : person.role}
                        onChange={(event) =>
                          roleMutation.mutate({ userId: person.id, role: event.target.value as StaffRole })
                        }
                      >
                        <option value="staff">Personel</option>
                        <option value="manager">Şube sorumlusu</option>
                        <option value="driver">Kurye</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td>
                      {person.lastSignInAt
                        ? new Date(person.lastSignInAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })
                        : 'Hiç'}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button type="button" className="button secondary" onClick={() => openProfile(person.id)}>
                          Görüntüle
                        </button>
                        <button
                          type="button"
                          className="button danger"
                          onClick={() => {
                            if (window.confirm(`${person.email} hesabından personel rolünü kaldırmak istediğinize emin misiniz?`)) {
                              deleteMutation.mutate(person);
                            }
                          }}
                        >
                          Rolü kaldır
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={existingUser ? 'Personel ekle' : 'Personel davet et'}
        wide
      >
        <form className="form-grid" onSubmit={form.handleSubmit(onSubmit)}>
          <label className="form-field">
            <span>E-posta</span>
            <div className="form-row center">
              <input type="email" {...form.register('email')} />
              <button
                type="button"
                className="button ghost"
                onClick={() => {
                  const emailValue = form.getValues('email');
                  if (!emailValue) {
                    form.setError('email', { message: 'Önce e-posta girin' });
                    return;
                  }
                  lookupMutation.mutate(emailValue);
                }}
                disabled={lookupMutation.isPending}
              >
                Ara
              </button>
            </div>
            {form.formState.errors.email ? (
              <span className="error-text">{form.formState.errors.email.message}</span>
            ) : null}
            {lookupMutation.isPending ? (
              <span className="helper-text">Kullanıcı aranıyor...</span>
            ) : null}
          </label>

          <div className="form-row">
            <label className="form-field">
              <span>Ad soyad</span>
              <input type="text" {...form.register('fullName')} />
            </label>
            <label className="form-field">
              <span>Telefon</span>
              <input type="tel" {...form.register('phone')} />
            </label>
          </div>

          <label className="form-field">
            <span>Restoran ataması</span>
            <select
              multiple
              disabled={roleValue === 'driver' || roleValue === 'admin'}
              {...form.register('restaurants')}
              value={form.watch('restaurants')}
              onChange={(event) => {
                const selected = Array.from(event.target.selectedOptions).map((opt) => opt.value);
                form.setValue('restaurants', selected, { shouldDirty: true });
              }}
            >
              {restaurants.map((restaurant) => (
                <option key={restaurant.id} value={restaurant.slug}>
                  {restaurant.name} ({restaurant.slug})
                </option>
              ))}
            </select>
            <p className="helper-text">
              Çoklu seçim yapabilirsiniz. Personel için en az bir restoran seçin; admin ve kurye rolleri için boş bırakın.
            </p>
            {form.formState.errors.restaurants ? (
              <span className="error-text">{form.formState.errors.restaurants.message}</span>
            ) : null}
          </label>

          <div className="form-row">
            <label className="form-field">
              <span>Rol</span>
              <select {...form.register('role')}>
                <option value="staff">Personel</option>
                <option value="manager">Şube sorumlusu</option>
                <option value="driver">Kurye</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            {!existingUser ? (
              <label className="form-field checkbox inline-checkbox">
                <input type="checkbox" {...form.register('inviteInsteadOfPassword')} />
                <span>E-posta daveti gönder</span>
              </label>
            ) : null}
          </div>

          {!existingUser && !form.watch('inviteInsteadOfPassword') ? (
            <label className="form-field">
              <span>Geçici parola</span>
              <input type="password" {...form.register('password')} />
              {form.formState.errors.password ? (
                <span className="error-text">{form.formState.errors.password.message}</span>
              ) : null}
            </label>
          ) : null}

          {form.formState.errors.root ? (
            <p className="error-text">{form.formState.errors.root.message}</p>
          ) : null}

          <div className="form-actions">
            <button type="button" className="button ghost" onClick={closeModal}>
              İptal
            </button>
            <button type="submit" className="button primary" disabled={createMutation.isPending}>
              {existingUser ? 'Personel ekle' : 'Personeli davet et'}
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        open={profileModalOpen}
        onClose={closeProfile}
        title="Personel profili"
      >
        {selectedStaff && selectedMeta ? (
          <>
            <div className="profile-grid">
              <div className="profile-row">
                <strong>Uygulama rolü</strong>
                <span>{selectedMeta.role || '—'}</span>
              </div>
              <div className="profile-row">
                <strong>Restoran rolü</strong>
                <span>{selectedMeta.restaurantRole || '—'}</span>
              </div>
              <div className="profile-row">
                <strong>Roller seti</strong>
                <span>{selectedMeta.roles.length > 0 ? selectedMeta.roles.join(', ') : '—'}</span>
              </div>
              <div className="profile-row">
                <strong>Bağlı restoranlar</strong>
                <span>{selectedMeta.restaurants.length > 0 ? selectedMeta.restaurants.join(', ') : '—'}</span>
              </div>
            </div>

            <pre className="code-block">
              {JSON.stringify(selectedStaff.raw ?? selectedStaff, null, 2)}
            </pre>
          </>
        ) : (
          <div className="empty-state">
            <strong>Profil yüklenemedi</strong>
          </div>
        )}
      </Modal>
    </>
  );
}
